import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip,
  FormControlLabel, Stack, TextField, Typography,
} from '@mui/material';
import systemConfigService, { GitHubConfigStatus } from '../../services/systemConfigService';

const errorDetail = (error: unknown, fallback: string) => {
  const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (!Array.isArray(detail)) return fallback;
  const labels: Record<string, string> = {
    repository_url: 'GitHub repository URL', token: 'GitHub token', clear_token: 'Token removal',
  };
  const messages: Record<string, string> = {
    missing: 'is required.', string_type: 'must be text.', string_too_long: 'is too long.',
    bool_type: 'must be true or false.', bool_parsing: 'must be true or false.',
  };
  const errors = detail.flatMap((entry: unknown) => {
    if (!entry || typeof entry !== 'object') return [];
    const { loc, type } = entry as { loc?: unknown; type?: unknown };
    const field = Array.isArray(loc) ? loc[1] : undefined;
    const label = typeof field === 'string' ? labels[field] : undefined;
    if (typeof label !== 'string') return [];
    const message = typeof type === 'string' ? messages[type] : undefined;
    return [`${label} ${typeof message === 'string' ? message : 'is invalid.'}`];
  });
  return errors.length ? errors.join(' ') : 'Invalid GitHub configuration. Check the repository URL and token, then try again.';
};

export default function GitHubSettingsPanel() {
  const [config, setConfig] = useState<GitHubConfigStatus | null>(null);
  const [repository, setRepository] = useState('');
  const [token, setToken] = useState('');
  const [clearToken, setClearToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    systemConfigService.getGitHubConfig().then((result) => {
      if (!active) return;
      setConfig(result);
      setRepository(result.repository_url);
    }).catch((err: unknown) => {
      if (active) setError(errorDetail(err, 'Failed to load GitHub configuration.'));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, []);

  const save = async () => {
    setLoading(true);
    setError('');
    setSaved(false);
    try {
      const result = await systemConfigService.updateGitHubConfig({
        repository_url: repository,
        ...(token.trim() ? { token: token.trim() } : {}),
        clear_token: clearToken,
      });
      setConfig(result);
      setRepository(result.repository_url);
      setToken('');
      setClearToken(false);
      setSaved(true);
    } catch (err) {
      setError(errorDetail(err, 'Failed to save GitHub configuration.'));
    } finally {
      setLoading(false);
    }
  };

  const dirty = config && (repository !== config.repository_url || Boolean(token.trim()) || clearToken);

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h6">GitHub Issue Reporting</Typography>
              {config && <Chip size="small" color={config.configured ? 'success' : 'warning'} label={config.configured ? 'Configured' : 'Not configured'} />}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Configure GITHUB_REPO and GITHUB_TOKEN for in-app issue tickets. These deployment-wide settings apply to reports from every organization, override deployment settings, and take effect immediately without a restart.
            </Typography>
            <Alert severity="info">
              Use a token with access to the selected repository and Issues: write permission. Contents: write is also needed for screenshot uploads when cloud storage is unavailable.
              Tokens are encrypted using the deployment encryption key and are never displayed after saving. After configuring, retry failed tickets from Issue Reports.
            </Alert>
            {error && <Alert severity="error">{error}</Alert>}
            {saved && <Alert severity="success">GitHub configuration saved. No restart required.</Alert>}
            <TextField
              label="GitHub repository URL (GITHUB_REPO)"
              placeholder="https://github.com/owner/repo"
              helperText="Accepts a GitHub repository URL or owner/repo. Leave empty to disable GitHub ticketing."
              value={repository}
              disabled={loading || !config}
              onChange={(event) => { setRepository(event.target.value); setSaved(false); }}
              fullWidth
            />
            <TextField
              label="GitHub token (GITHUB_TOKEN)"
              type="password"
              autoComplete="new-password"
              value={token}
              disabled={loading || !config || clearToken}
              onChange={(event) => { setToken(event.target.value); setSaved(false); }}
              helperText={config?.token_configured ? 'Token configured. Leave blank to keep the existing token, or enter a replacement.' : 'No token configured. Enter a repository-scoped GitHub personal access token.'}
              fullWidth
            />
            {config?.token_configured && (
              <FormControlLabel
                label="Remove configured token and disable GitHub ticketing"
                control={<Checkbox checked={clearToken} disabled={loading} onChange={(event) => { setClearToken(event.target.checked); setToken(''); setSaved(false); }} />}
              />
            )}
            <Box><Button variant="contained" disabled={loading || !dirty} onClick={save}>Save GitHub configuration</Button></Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
